import { loadIndexes } from '@data/indexes';
import type { Arme, Armure } from '@data/schemas';
import { html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';

type SectionId = 'contact' | 'distance' | 'armures' | 'boucliers';

export async function renderEquipementPage(): Promise<unknown> {
  const idx = await loadIndexes();
  const armesC = idx.bdd.equipement.armes_contact;
  const armesD = idx.bdd.equipement.armes_distance;
  const armures = idx.bdd.equipement.armures;
  const boucliers = idx.bdd.equipement.boucliers;

  const formatPortee = (a: Arme): string => {
    if (a.portee_normale != null) return `${a.portee_normale} m`;
    if (a.portee_lancer != null) return `${a.portee_lancer} m (lancer)`;
    return '-';
  };

  const formatDm = (a: Arme): string => {
    if (a.dm) return a.dm;
    if (a.dm_une_main && a.dm_deux_mains) return `${a.dm_une_main} / ${a.dm_deux_mains}`;
    if (a.dm_distance) return a.dm_distance;
    return '-';
  };

  const armeRow = (a: Arme) => html`
    <tr class="equip-row">
      <td class="equip-cell-name">${a.nom}</td>
      <td class="equip-cell-num">${formatDm(a)}</td>
      <td>${a.type_dm}</td>
      <td class="equip-cell-num">${formatPortee(a)}</td>
      <td class="equip-cell-num">${a.prix ?? '-'}</td>
    </tr>
  `;

  const armureRow = (a: Armure) => html`
    <tr class="equip-row">
      <td class="equip-cell-name">${a.nom}</td>
      <td class="equip-cell-num">+${a.bonus_def}</td>
      <td class="equip-cell-num">${a.agi_max}</td>
      <td class="equip-cell-num">${a.malus_armure}</td>
      <td class="equip-cell-num">${a.prix ?? '-'}</td>
    </tr>
  `;

  const refs: Record<SectionId, ReturnType<typeof createRef<HTMLElement>>> = {
    contact: createRef<HTMLElement>(),
    distance: createRef<HTMLElement>(),
    armures: createRef<HTMLElement>(),
    boucliers: createRef<HTMLElement>(),
  };

  const onTabChange = (e: CustomEvent<{ id: string }>) => {
    const target = e.detail.id as SectionId;
    for (const id of Object.keys(refs) as SectionId[]) {
      const el = refs[id].value;
      if (el) el.hidden = id !== target;
    }
  };

  return html`
    <div class="equip-page">
      <header class="equip-header">
        <h1 class="equip-title" data-morph data-morph-duration="1500">Equipement</h1>
        <p class="equip-subtitle">${armesC.length + armesD.length} armes · ${armures.length} armures · ${boucliers.length} boucliers</p>
      </header>

      <cof2-tabs
        .tabs=${[
          { id: 'contact', label: `Contact (${armesC.length})` },
          { id: 'distance', label: `Distance (${armesD.length})` },
          { id: 'armures', label: `Armures (${armures.length})` },
          { id: 'boucliers', label: `Boucliers (${boucliers.length})` },
        ]}
        active="contact"
        @cof2-tab-change=${onTabChange}
      ></cof2-tabs>

      <div class="equip-panels">
        <section ${ref(refs.contact)} id="tabpanel-contact" role="tabpanel" aria-labelledby="tab-contact">
          <div class="equip-table-wrap">
            <table class="equip-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>DM</th>
                  <th>Type</th>
                  <th>Portee</th>
                  <th>Prix</th>
                </tr>
              </thead>
              <tbody>${armesC.map(armeRow)}</tbody>
            </table>
          </div>
        </section>

        <section ${ref(refs.distance)} id="tabpanel-distance" role="tabpanel" aria-labelledby="tab-distance" hidden>
          <div class="equip-table-wrap">
            <table class="equip-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>DM</th>
                  <th>Type</th>
                  <th>Portee</th>
                  <th>Prix</th>
                </tr>
              </thead>
              <tbody>${armesD.map(armeRow)}</tbody>
            </table>
          </div>
        </section>

        <section ${ref(refs.armures)} id="tabpanel-armures" role="tabpanel" aria-labelledby="tab-armures" hidden>
          <div class="equip-table-wrap">
            <table class="equip-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>DEF</th>
                  <th>AGI max</th>
                  <th>Malus</th>
                  <th>Prix</th>
                </tr>
              </thead>
              <tbody>${armures.map(armureRow)}</tbody>
            </table>
          </div>
        </section>

        <section ${ref(refs.boucliers)} id="tabpanel-boucliers" role="tabpanel" aria-labelledby="tab-boucliers" hidden>
          <div class="equip-table-wrap">
            <table class="equip-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Bonus DEF</th>
                </tr>
              </thead>
              <tbody>
                ${boucliers.map(
                  (b) => html`
                  <tr class="equip-row">
                    <td class="equip-cell-name">${b.nom}</td>
                    <td class="equip-cell-num">+${b.bonus_def}</td>
                  </tr>
                `,
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  `;
}
